import { Component, NgZone, OnInit } from '@angular/core';
import { Select } from '@ngxs/store';
import { OrganizationState } from '@state/organization.state';
import { AnalyticCategoryWithEntities } from 'app/@types/shared/analytical-categories-entities';
import { AnalyticalCategoryDto } from 'app/@types/dto/analytical-category-dto.model';
import { OrganizationEntityDto } from 'app/@types/dto/organization-entity-dto.model';
import { combineLatest, Observable, startWith } from 'rxjs';
import { ToastService } from '@services/common/toast.service';
import { OrganizationDto } from 'app/@types/dto/organization-dto.model';
import { CustomTranslateService } from '@services/common/custom-translate.service';

@Component({
    selector: 'app-settings-form-analytic-categories',
    templateUrl: './settings-form-analytic-categories.component.html',
    styleUrls: ['./settings-form-analytic-categories.component.scss'],
    standalone: false,
})
export class SettingsFormAnalyticCategoriesComponent implements OnInit {
    @Select(OrganizationState.getAnalyticCategories) analyticCategories!: Observable<AnalyticalCategoryDto[]>;
    @Select(OrganizationState.getEntities) entities!: Observable<OrganizationEntityDto[]>;
    @Select(OrganizationState.getOrganization) organization!: Observable<OrganizationDto>;

    public organizationId: string = '';
    public addingEditingMode: boolean = false;
    public categories: AnalyticCategoryWithEntities[] = [];
    public selectedAnalyticCategory: AnalyticCategoryWithEntities | undefined = undefined;

    constructor(
        private toastService: ToastService,
        private ngZone: NgZone,
        private translateService: CustomTranslateService,
    ) {}

    ngOnInit(): void {
        //Add onlangchange
        combineLatest([
            this.analyticCategories,
            this.entities,
            this.organization,
            this.translateService.onLangChange.pipe(startWith(null)),
        ]).subscribe(([categories, entities, organization]) => {
            if (organization && organization.uid) {
                this.organizationId = organization.uid;
                this.categories = categories.map((category) => ({
                    ...category,
                    entities: category.entityIds?.map((entityId) => {
                        const entity = entities.find((e) => e.id === entityId);
                        if (!entity) {
                            throw new Error(`Entity not found in the entities from organizationState`);
                        }
                        return entity;
                    }),
                }));
                this.categories = this.categories.map((c) => ({
                    ...c,
                    name: c.default ? this.translateService.instant('section.' + c.name + '.title') : c.name,
                }));
                this.categories.sort((a, b) => a.name.localeCompare(b.name)).sort((a, b) => +a.default - +b.default);
            }
        });
    }

    public addAnalyticalCategory(category: AnalyticCategoryWithEntities | undefined = undefined): void {
        this.selectedAnalyticCategory = category;
        this.addingEditingMode = true;
    }

    onCloseEditActivity(event: { translateKey: string; status: boolean } | undefined) {
        this.addingEditingMode = false;
        if (event) {
            this.ngZone.run(() => {
                if (event.status) {
                    this.toastService.showSuccess({
                        details: event.translateKey,
                    });
                } else {
                    this.toastService.showError({
                        details: event.translateKey,
                    });
                }
            });
        }
    }

    //#region Helper Methods

    /**
     * Generates a tooltip string from an array of entities.
     * Supports both strings and objects with a 'name' property.
     * Only includes entities beyond the first two.
     *
     * @param entities - An array of either strings or objects containing a 'name' field
     * @returns A comma-separated string of the remaining entity names (after the first two)
     */
    getTooltipEntities(entities: (string | { name: string })[]): string {
        return entities
            .slice(2) // Skip the first two entities (already displayed visually)
            .map(
                (e) =>
                    typeof e === 'string'
                        ? e // If entity is a string, use it directly
                        : e.name, // If it's an object, extract the 'name' property
            )
            .join(', '); // Combine remaining names into a single string separated by commas
    }
    //#endregion
}
